/**
 * ArBundleContinuationRow
 *
 * Opens the run of members a pagination boundary cut off from its header
 * (SYD-2430): the collapse caret, the bundle name, the type tag and which way the
 * run continues. The checkbox, grip and actions menu stay with the header, on the
 * page before this one. `ArTableBundleRows` renders it, and only on screen — PDF
 * export loads every row, so no continuation item is ever minted there.
 *
 * It stands for no row — never in `renderedRowIds`, so not sortable, not
 * selectable, no drop target. Collapse is keyed by `bundleId`, so this caret and
 * the header's toggle one bundle from two pages: hence every label distinct from
 * the header's, and hence `disabled`, which must match its posture.
 *
 * `stickyLabelLeft` is the header's own first-cell offset, so the message pins
 * where the header's name pins; `undefined` means no column is frozen and both
 * rows scroll away together. It is gated on `stickyFirstColumn` as well, because
 * a pinned label above an unpinned, scrolling caret is worse than neither.
 *
 * The caret cell's own `colorBgLayout` is opaque, so it takes no `backdropFilter`
 * — a blur over an opaque paint blurs nothing and buys a compositing layer
 * (SYD-2439). The frosted 56% mix belongs to `ArCell` and to the header's frozen
 * data cells, where the background really is see-through.
 *
 * In card mode (below `md`) the caret moves into the band and the band spans the
 * whole card width from line 1, so it never lands in the narrow actions track
 * and widens that track in every card on the page. The band places itself with
 * `gridColumnStart`/`gridColumnEnd` longhands in both modes, so switching modes
 * never clears a shorthand over a longhand.
 */

import { CSSProperties, FC, memo } from 'react';
import { Button, Tag, Typography, theme } from 'antd';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { ArBundleType } from '#types/System.Artifacts.types';

const { Text } = Typography;

type ArBundleContinuationRowProps = {
  bundleId: string;
  bundleType: ArBundleType;
  label: string;
  continuesOnNextPage: boolean;
  collapsed: boolean;
  onToggleCollapsed: (bundleId: string) => void;
  disabled: boolean;
  stickyFirstColumn?: boolean;
  stickyLabelLeft?: number;
  cardMode?: boolean;
};

const ArBundleContinuationRow: FC<ArBundleContinuationRowProps> = ({
  bundleId,
  bundleType,
  label,
  continuesOnNextPage,
  collapsed,
  onToggleCollapsed,
  disabled,
  stickyFirstColumn = true,
  stickyLabelLeft,
  cardMode = false,
}) => {
  const { token } = theme.useToken();

  const bundleTypeLabel = bundleType === 'kit' ? 'Kit' : 'Group';

  const caretCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: token.colorBgLayout,
    borderRight: `1px solid ${token.colorBorderSecondary}`,
    boxSizing: 'border-box',
  };

  if (stickyFirstColumn) {
    caretCellStyle.position = 'sticky';
    caretCellStyle.left = 0;
    caretCellStyle.zIndex = 20;
  }

  /**
   * Two cells only: the caret, and one band spanning every remaining track. The
   * band holds the whole message, so per-column cells would be empty duplicates
   * — and a message confined to column 0 either stretches that track past page
   * 1's width or, under a user width, is clipped by `overflow: hidden`. The band
   * therefore spans the tracks after the caret, and its contents pin instead: a
   * band as wide as the grid scrolls its own text off the left edge, while the
   * header's label cell never leaves the viewport.
   */
  const bandStyle: CSSProperties = {
    gridColumnStart: cardMode ? 1 : 2,
    gridColumnEnd: -1,
    position: 'relative',
    height: token.controlHeight,
    minHeight: token.controlHeight,
    display: 'flex',
    alignItems: 'center',
    gap: token.paddingXS,
    borderRadius: 0,
    boxSizing: 'border-box',
    minWidth: 0,
    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
  };

  const labelGroupStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: token.paddingXS,
    minWidth: 0,
  };

  if (stickyFirstColumn && stickyLabelLeft !== undefined) {
    labelGroupStyle.position = 'sticky';
    labelGroupStyle.left = stickyLabelLeft;
  }

  const caret = (
    <Button
      aria-label={`artable-toggle-bundle-collapse-continuation-${bundleId}`}
      type="text"
      size="small"
      disabled={disabled}
      icon={collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
      onClick={(e) => {
        e.stopPropagation();
        onToggleCollapsed(bundleId);
      }}
      style={{ padding: 0 }}
    />
  );

  return (
    <div
      aria-label={`artable-bundle-continuation-${bundleId}`}
      role="row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumn: '1 / -1',
        backgroundColor: token.colorBgLayout,
        borderTop: `1px dashed ${token.colorBorder}`,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        boxSizing: 'border-box',
      }}
    >
      {!cardMode && (
        <div role="cell" style={caretCellStyle}>
          {caret}
        </div>
      )}

      <div
        aria-label={`artable-bundle-continuation-cell-${bundleId}`}
        role="cell"
        style={bandStyle}
      >
        {cardMode && caret}
        <div style={labelGroupStyle}>
          <Text strong style={{ minWidth: 0 }}>
            {label}
          </Text>
          <Text
            aria-label={`artable-bundle-continued-from-previous-page-${bundleId}`}
            type="secondary"
            style={{ fontSize: token.fontSizeSM }}
          >
            Continued from previous page
          </Text>
          {continuesOnNextPage && (
            <Text
              aria-label={`artable-bundle-continues-on-next-page-continuation-${bundleId}`}
              type="secondary"
              style={{ fontSize: token.fontSizeSM }}
            >
              Continues on next page
            </Text>
          )}
          <Tag
            aria-label={`artable-bundle-continuation-type-${bundleId}`}
            style={{ marginInlineEnd: 0 }}
          >
            {bundleTypeLabel}
          </Tag>
        </div>
      </div>
    </div>
  );
};

export default memo(ArBundleContinuationRow);
